import * as React from "react";
import { Alert, AlertColor, Box, Stack } from "@mui/material";
import {
    Participant,
    ParticipantAssessmentStatus,
    ParticipantInstrumentStatus,
} from "../../../../../services/types/enterpriseParticipantApiTypes";
import ScoringRequestDrawer from "./components/scoringRequestDrawer/scoringRequestDrawer";
import CclAlertDialog from "../../../../../components/common/cclAlertDialog";
import ParticipantContactDrawer from "./components/participantContactDrawer/participantContactDrawer";
import {
    useSendRaterRemindersMutation,
    useResendRaterInvitationsMutation,
    useGetCclParticipantsByEventKeyQuery,
    useSendParticipantRemindersMutation,
    useResendParticipantInvitationsMutation,
    useGetSessionParticipantsAssessmentStatusQuery,
    useGetParticipantAssessmentStatusQuery,
} from "../../../../../services/cclTokenedEnterpriseParticipantApi";
import CclAlertSnackbar from "../../../../../components/common/cclAlertSnackbar";
import { useNavigate } from "react-router-dom";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";
import {
    AccessEventRegistrationDetails,
    AccessEventSessionDetails,
} from "../../../../../services/types/accessEventTypes";
import ParticipantKeyDatesDrawer from "./components/participantKeyDatesDrawer/participantKeyDatesDrawer";
import {
    eGroupIds,
    UserClaimsService,
} from "../../../../../services/currentUserService/currentUserService";
import AssessmentStatusDataGrid from "./components/assessmentStatusDataGrid";
import AdditionalAssessmentsDrawerNew from "./components/additionalInstrumentsDrawer/additionalAssessmentsDrawerNew";
import ComponentLoader from "../../../../../components/common/componentLoader";
import AssessmentStatusPdf from "./components/AssessmentStatusPdf";
import { useGetSessionSetUpByEventKeyQuery } from "../../../../../services/cclTokenedSessionApi";
import RequestScoringDrawer from "./components/requestScoringDrawer/requestScoringDrawer";
import {
    AP_SURVEY_PLATFORM_KEYS,
    SessionDetailType,
} from "../../../../../services/types/sessionDetailTypes";
import UninvitedParticipantsCard from "./components/uninvitedParticipantsCard";

const ABOUT_ME_INSTRUMENT_ID = 2946;

export type ScoringStatusSource = "session" | "registration" | "my-access";

export interface ScoringStatusSubPanelProps {
    sessionKey: number | undefined;
    sessionCode: string | undefined;
    handleJournalEntry?: (keys: string[]) => void;
    participant?: Participant;
    readOnly?: boolean;
    hostPlatformKey: number;
    surveyPlatformKey: number;
    startDate: Date | null;
    isProductSale?: boolean;
    updateHidePlatformDates?: (value: boolean) => void;
    updateHideRaterManagement?: (value: boolean) => void;
    hideAboutMe?: boolean | null;
    onAboutMeVisibilityChange?: (hidden: boolean) => void;
    source: ScoringStatusSource;
    /** Session details; when provided with source "session", the Un-Invited Participants card is shown. */
    sessionDetails?: SessionDetailType;
    showSendParticipantsEmail?: boolean;
    onSendParticipantsEmail?: (esiKeys: number[]) => void;
}

const ScoringStatusSubPanel: React.FC<ScoringStatusSubPanelProps> = (props) => {
    const claimsService = new UserClaimsService();
    const [showParticipantKeyDatesDrawer, setShowParticipantKeyDatesDrawer] =
        React.useState<boolean>(false);
    const [showPdfPreview, setShowPdfPreview] = React.useState<boolean>(false);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const [showScoringRequestDrawer, setShowScoringRequestDrawer] = React.useState<boolean>(false);
    const [showParticipantContactDrawer, setShowParticipantContactDrawer] =
        React.useState<boolean>(false);
    const [showAdditionalInstrumentsDrawer, setShowAdditionalInstrumentsDrawer] =
        React.useState<boolean>(false);
    const [additionalInstrumentsEsiKey, setAdditionalInstrumentsEsiKey] = React.useState<number>(0);
    const [additionalInstruments, setAdditionalInstruments] = React.useState<
        ParticipantInstrumentStatus[]
    >([]);
    const [selectedParticipant, setSelectedParticipant] = React.useState<Participant | null>(null);
    const [showAlertDialog, setShowAlertDialog] = React.useState<boolean>(false);
    const [participantsToScore, setParticipantsToScore] = React.useState<
        ParticipantAssessmentStatus[]
    >([]);
    const [selectedParticipantsIdsForPdf, setSelectedParticipantsIdsForPdf] = React.useState<
        number[]
    >([]);
    const aboutMeInitiallyHidden = props.isProductSale === true && props.hideAboutMe === false;
    const [hiddenAssessments, setHiddenAssessments] = React.useState<number[]>(
        aboutMeInitiallyHidden ? [ABOUT_ME_INSTRUMENT_ID] : [],
    );

    const [sendRaterReminders] = useSendRaterRemindersMutation();
    const [resendRaterInvitations] = useResendRaterInvitationsMutation();

    const [openRequestDrawer, setOpenRequestDrawer] = React.useState<boolean>(false);
    const [instrumentKey, setInstrumentKey] = React.useState<number>(0);
    const [selectedEsiKeys, setSelectedEsiKeys] = React.useState<number[]>([]);

    const {
        data: programStatus,
        isLoading,
        isSuccess,
        isError,
    } = useGetSessionParticipantsAssessmentStatusQuery(props.sessionKey as number, {
        skip: props.sessionKey === undefined || props.participant !== undefined,
    });

    const { data: programSetup } = useGetSessionSetUpByEventKeyQuery(
        props.sessionKey?.toString() ?? (props.participant?.esiKey?.toString() as string),
        {
            skip: props.sessionKey === undefined && props.participant === undefined,
        },
    );

    const {
        data: participantStatus,
        isLoading: paxIsLoading,
        isSuccess: paxIsSuccess,
        isError: paxIsError,
    } = useGetParticipantAssessmentStatusQuery(props.participant?.esiKey as number, {
        skip: props.participant === undefined,
    });

    React.useEffect(() => {
        if (!isSuccess && !paxIsSuccess) return;
        const status = isSuccess ? programStatus : participantStatus;
        const shouldHide =
            status?.instruments == null ||
            status?.instruments.length === 0 ||
            status?.participants == null ||
            status?.participants.length === 0;
        props.updateHidePlatformDates?.(shouldHide);
        //@ts-ignore
    }, [isSuccess, paxIsSuccess, programStatus, participantStatus, props.updateHidePlatformDates]);

    const { data: sessionParticipants } = useGetCclParticipantsByEventKeyQuery(
        props.sessionKey?.toString() ?? "",
        {
            skip: props.sessionKey === undefined,
        },
    );

    const [resendParticipantInvitations] = useResendParticipantInvitationsMutation();
    const [sendParticipantsReminders] = useSendParticipantRemindersMutation();
    const navigate = useNavigate();
    const { logEvent } = useLogAccessEvent();

    const isAdmin = claimsService.IsUserInGroups([
        eGroupIds.eCCLAdmin,
        eGroupIds.eCCLSuperAdmin,
        eGroupIds.eNWAAdmin,
        eGroupIds.eClientAdmin,
    ]);

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const openScoringRequestDrawer = (selectedParticipantIds: number[]) => {
        if (selectedParticipantIds.length === 0) {
            setParticipantsToScore([]);
            return;
        }

        const selectedParticipants = selectedParticipantIds.reduce(function (
            acc: ParticipantAssessmentStatus[],
            curr: number,
        ) {
            const pax = programStatus?.participants.find((p) => p.esiKey === curr);
            if (pax !== undefined) {
                acc.push(pax);
            }
            return acc;
        }, []);

        setParticipantsToScore([...selectedParticipants]);
        setShowScoringRequestDrawer(true);
    };

    const openRequestScoringDrawer = (esiKeys: number[]) => {
        setSelectedEsiKeys(esiKeys);
        setOpenRequestDrawer(true);
    };

    const handleScoringSuccess = (esiKeys: number[]) => {
        const message =
            props.source === "session"
                ? "Scoring Request(s) Processed"
                : "Participant Scoring Request Processed";
        ShowSnackbarElement(message, "success");

        if (props.source === "session") {
            const eventData: AccessEventSessionDetails = {
                sessionID: props.sessionKey?.toString() ?? "",
                projectCode: props.sessionCode ?? "",
            };
            logEvent("ScoringRequested", eventData);
        } else {
            esiKeys.forEach((esiKey) => {
                const participant =
                    props.participant?.esiKey === esiKey
                        ? props.participant
                        : sessionParticipants?.find((p) => p.esiKey === esiKey);
                const eventData: AccessEventRegistrationDetails = {
                    esiKey: esiKey.toString(),
                    imKey: participant?.imKey?.toString(),
                    email: participant?.emailAddress,
                    ...(props.sessionCode != null && { projectCode: props.sessionCode }),
                };
                logEvent("ScoringRequested", eventData);
            });
        }
    };

    const handleScoringFailure = () => {
        const message =
            props.source === "session"
                ? "Scoring Request Failed"
                : "Participant Scoring Request Failed";
        ShowSnackbarElement(message, "error");
    };

    const openAdditionalInstrumentsDrawer = (esiKey: number) => {
        let status = props.participant === undefined ? programStatus : participantStatus;
        let participant = status?.participants?.find((p) => p.esiKey === esiKey) ?? null;

        setAdditionalInstrumentsEsiKey(esiKey);
        setAdditionalInstruments(participant?.additionalInstruments ?? []);
        setShowAdditionalInstrumentsDrawer(true);
    };

    const handleScoreRequest = (scoreInstrumentIds: number[]) => {
        //TODO: implement actual scoring request later, but for now show a dialog
        setShowScoringRequestDrawer(false);
        setShowAlertDialog(true);
    };

    const handleUpdateInstrumentKey = React.useCallback(
        (esikey: number) => {
            const status = props.participant === undefined ? programStatus : participantStatus;
            const participant = status?.participants?.find((p) => p.esiKey === esikey) ?? null;
            let instrument = 0;
            if (participant == null) return;
            if (participant.instruments.length > 0) {
                instrument = participant.instruments[0].instrumentId;
            } else {
                instrument = 0;
            }
            if (instrument == null) return;
            // check to see if its the about me instrument, if so skip or ignore
            if (instrument === ABOUT_ME_INSTRUMENT_ID) {
                if (participant.instruments.length > 1) {
                    instrument = participant.instruments[1].instrumentId;
                } else {
                    instrument = 0;
                }
            }
            setInstrumentKey(instrument);
        },
        [props.participant, programStatus, participantStatus],
    );

    React.useEffect(() => {
        const status = programStatus ?? participantStatus;
        const firstWithInstrument = status?.participants?.find((p) =>
            p.instruments.some((i) => i.instrumentId !== ABOUT_ME_INSTRUMENT_ID),
        );
        if (firstWithInstrument) {
            handleUpdateInstrumentKey(firstWithInstrument.esiKey);
        }
    }, [programStatus, participantStatus, handleUpdateInstrumentKey]);

    const handleEvent = async (event: string, participantIds: number[]) => {
        let participant = sessionParticipants?.find((p) => p.esiKey === participantIds[0]);

        if (participant) {
            setSelectedParticipant(participant);
        }

        switch (event) {
            case "viewparticipantcontactinfo":
                if (participant == null) return;
                setSelectedParticipant(participant);
                setShowParticipantContactDrawer(true);
                break;

            case "sendraterreminders":
            case "sendraterreminder":
                await sendRaterReminders(participantIds)
                    .unwrap()
                    .then(() => {
                        ShowSnackbarElement("Reminder(s) Resent", "success");
                        sendNotificationEvent("RaterRemindersResent", participantIds);
                    })
                    .catch((error) => {
                        ShowSnackbarElement("Reminder Resend Failed", "error");
                    });

                break;

            case "viewassessmentlist":
                navigate(`/registration/${participantIds[0]}/1?subTab=0`);
                break;

            case "resendraterinvitations":
            case "resendraterinvitation":
                await resendRaterInvitations(participantIds)
                    .unwrap()
                    .then(() => {
                        ShowSnackbarElement("Invitation(s) Resent", "success");
                        sendNotificationEvent("RaterInvitationsResent", participantIds);
                    })
                    .catch((error) => {
                        ShowSnackbarElement("Invitation Resend Failed", "error");
                    });
                break;

            case "createjournalentry":
                if (!props.handleJournalEntry) return;
                props.handleJournalEntry(participantIds.map(String));
                break;

            case "editkeydates":
                if (participant == null) return;
                setSelectedParticipant(participant);
                setShowParticipantKeyDatesDrawer(true);
                break;

            case "resendinvitation":
            case "resendinvitations":
                if (participantIds.length === 0 || props.sessionKey == null) return;

                await resendParticipantInvitations({
                    participantEsiKeys: participantIds,
                    esKey: props.sessionKey,
                })
                    .unwrap()
                    .then(() => {
                        ShowSnackbarElement("Invitation(s) Resent", "success");
                        sendNotificationEvent("ParticipantInvitationsResent", participantIds);
                    })
                    .catch(() => {
                        ShowSnackbarElement("Failed to send invitations", "error");
                    });
                break;

            case "sendreminder":
            case "sendreminders":
                if (participantIds.length === 0) return;
                sendParticipantsReminders(participantIds)
                    .unwrap()
                    .then(() => {
                        ShowSnackbarElement("Reminder(s) Resent", "success");
                        if (sessionParticipants == null) return;
                        sessionParticipants
                            .filter((p) => participantIds.includes(p.esiKey))
                            ?.forEach((p) => {
                                let eventData: AccessEventRegistrationDetails = {
                                    esiKey: p.esiKey.toString(),
                                    imKey: p.imKey.toString(),
                                    email: p.emailAddress,
                                };
                                logEvent("ParticipantRemindersResent", eventData);
                            });
                    })
                    .catch(() => {
                        ShowSnackbarElement("Resend Reminder(s) Failed", "error");
                    });
                break;

            case "export":
                setSelectedParticipantsIdsForPdf(participantIds);
                setShowPdfPreview(true);
                break;

            case "sendparticipantsemail":
                props.onSendParticipantsEmail?.(participantIds);
                break;

            default:
                break;
        }
    };

    const sendNotificationEvent = (eventName: string, ids: number[]) => {
        var status = programStatus != null ? programStatus : participantStatus;
        if (status == null || status.participants == null) return;
        for (let i = 0; i < ids.length; i++) {
            let p = status.participants.find((p) => p.esiKey === ids[i]);
            if (p == null) continue;
            let eventData: AccessEventRegistrationDetails = {
                esiKey: p.esiKey.toString(),
                imKey: p.imKey.toString(),
                email: p.email,
            };
            logEvent(eventName, eventData);
        }
    };

    React.useEffect(() => {
        const currentStatus = programStatus != null ? programStatus : participantStatus;
        const hasNoInstruments =
            currentStatus?.instruments == null || currentStatus.instruments.length === 0;
        const hasNoParticipants =
            currentStatus?.participants == null || currentStatus.participants.length === 0;
        const shouldHide = (isSuccess || paxIsSuccess) && (hasNoInstruments || hasNoParticipants);
        props.updateHidePlatformDates?.(shouldHide);
        props.updateHideRaterManagement?.(shouldHide);
        // @ts-ignore
    }, [
        isSuccess,
        paxIsSuccess,
        programStatus?.instruments?.length,
        programStatus?.participants?.length,
        participantStatus?.instruments?.length,
        participantStatus?.participants?.length,
        props.updateHidePlatformDates,
        props.updateHideRaterManagement,
    ]);

    if (isLoading || paxIsLoading) {
        return <ComponentLoader msg="Loading assessment status details..." />;
    }

    const assessmentVisibilityChange = (visible: boolean, instrumentId: number) => {
        if (visible) {
            setHiddenAssessments(hiddenAssessments.filter((id) => id !== instrumentId));
        } else {
            setHiddenAssessments([...hiddenAssessments, instrumentId]);
        }
        if (instrumentId === ABOUT_ME_INSTRUMENT_ID && props.isProductSale) {
            props.onAboutMeVisibilityChange?.(!visible);
        }
    };

    // registrations-based, so it is shown even when there is no assessment status data yet
    const uninvitedParticipantsCard =
        props.source === "session" && props.sessionDetails != null ? (
            <UninvitedParticipantsCard session={props.sessionDetails} />
        ) : null;

    if (isSuccess || paxIsSuccess) {
        var status = isSuccess ? programStatus : participantStatus;
        if (
            status?.instruments == null ||
            status?.instruments.length === 0 ||
            status?.participants == null ||
            status?.participants.length === 0
        ) {
            return (
                <Box width={1}>
                    <Alert severity="info">
                        No assessment status information found for this session.
                    </Alert>
                    {uninvitedParticipantsCard}
                </Box>
            );
        }

        var gridParticipants =
            isLoading || status === undefined || status.participants === undefined
                ? []
                : props.participant === undefined
                  ? status.participants.filter((p) => p.instruments.length > 0)
                  : status.participants
                        .filter((p) => p.instruments.length > 0)
                        .filter((p) => p.esiKey === props.participant?.esiKey);

        var pdfParticipants =
            selectedParticipantsIdsForPdf.length > 0
                ? gridParticipants.filter((p) => selectedParticipantsIdsForPdf.includes(p.esiKey))
                : gridParticipants;

        // default case specified here
        let scoringMode: "request" | "score" | "none" = "none";

        if (props.source !== "my-access") {
            if (props.isProductSale) {
                if (
                    claimsService.IsUserInGroups([
                        eGroupIds.eClientAdmin,
                        eGroupIds.eCCLAdmin,
                        eGroupIds.eCCLSuperAdmin,
                    ])
                ) {
                    scoringMode = "request";
                }
            } else {
                if (claimsService.IsUserInGroups([eGroupIds.eCCLAdmin, eGroupIds.eCCLSuperAdmin, eGroupIds.eNWAAdmin])) {
                    scoringMode = "score";
                }
            }
        }

        return (
            <Box width={1}>
                {isLoading || paxIsLoading ? null : (
                    <React.Fragment>
                        <CclAlertDialog
                            title="Scoring Function Not Yet Implemented"
                            msg="Scoring Function Not Yet Implemented"
                            open={showAlertDialog}
                            onOk={() => setShowAlertDialog(false)}
                            severity={"error"}
                        />
                        <ScoringRequestDrawer
                            open={showScoringRequestDrawer}
                            onClose={() => setShowScoringRequestDrawer(false)}
                            onScore={handleScoreRequest}
                            selectedParticipantCount={participantsToScore.length}
                            instruments={status.instruments}
                        />
                    </React.Fragment>
                )}
                {showParticipantKeyDatesDrawer && selectedParticipant != null ? (
                    <ParticipantKeyDatesDrawer
                        open={true}
                        onClose={() => setShowParticipantKeyDatesDrawer(false)}
                        participant={selectedParticipant}
                        showSnackbar={ShowSnackbarElement}
                        readOnly={props.source === "my-access"}
                    />
                ) : null}
                {showParticipantContactDrawer && selectedParticipant != null ? (
                    <ParticipantContactDrawer
                        open={true}
                        onClose={() => setShowParticipantContactDrawer(false)}
                        esiKey={selectedParticipant.esiKey}
                    />
                ) : null}
                {showAdditionalInstrumentsDrawer && additionalInstrumentsEsiKey !== 0 ? (
                    <AdditionalAssessmentsDrawerNew
                        open={true}
                        onClose={() => setShowAdditionalInstrumentsDrawer(false)}
                        esiKey={additionalInstrumentsEsiKey}
                        additionalInstruments={additionalInstruments}
                    />
                ) : null}
                {showSnackbar ? (
                    <CclAlertSnackbar
                        open={true}
                        onClose={() => setShowSnackbar(false)}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                    />
                ) : null}
                {openRequestDrawer ? (
                    <RequestScoringDrawer
                        open={openRequestDrawer}
                        instrumentKey={instrumentKey}
                        esiKeys={selectedEsiKeys}
                        sessionKey={props.sessionKey ?? 0}
                        onClose={() => setOpenRequestDrawer(false)}
                        onSuccess={handleScoringSuccess}
                        onFailure={handleScoringFailure}
                        source={props.source}
                    />
                ) : null}

                <AssessmentStatusDataGrid
                    scoringMode={scoringMode}
                    isAdmin={isAdmin}
                    instruments={status.instruments}
                    participants={gridParticipants}
                    isRegistration={props.participant !== undefined}
                    isLoading={isLoading}
                    sessionKey={props.sessionKey}
                    sessionCode={props.sessionCode}
                    onScorePressed={openScoringRequestDrawer}
                    onRequestScorePressed={openRequestScoringDrawer}
                    handleEvent={handleEvent}
                    handleAdditionalClick={openAdditionalInstrumentsDrawer}
                    assessmentVisibilityChange={assessmentVisibilityChange}
                    readOnly={props.readOnly}
                    hideParticipantEmailActions={props.readOnly || props.hostPlatformKey !== 9}
                    hideRaterActions={props.readOnly || !AP_SURVEY_PLATFORM_KEYS.includes(props.surveyPlatformKey)}
                    showSendParticipantsEmail={props.showSendParticipantsEmail ?? false}
                    allowExport={claimsService.IsUserInGroups([
                        eGroupIds.eCCLAdmin,
                        eGroupIds.eCCLSuperAdmin,
                        eGroupIds.eNWAAdmin,
                        eGroupIds.eClientAdmin,
                    ])}
                    showSnackbar={ShowSnackbarElement}
                    isProductSale={props.isProductSale}
                    hideAboutMe={props.hideAboutMe}
                />

                {uninvitedParticipantsCard}

                {showPdfPreview ? (
                    <AssessmentStatusPdf
                        open={true}
                        onClose={() => setShowPdfPreview(false)}
                        sessionTitle={
                            programStatus?.sessionTitle ?? participantStatus?.sessionTitle ?? ""
                        }
                        sessionCode={props.sessionCode ?? ""}
                        sessionKey={props.sessionKey ?? 0}
                        instruments={status.instruments.filter(
                            (i) => !hiddenAssessments.includes(i.id),
                        )}
                        gridParticipants={pdfParticipants}
                        startDate={props.startDate}
                        surveyCompletionDate={
                            programSetup?.sessionProgramData?.surveyCompletionDate ?? null
                        }
                        preworkInvitationDate={
                            programSetup?.sessionProgramData?.preworkInvitationDate ?? null
                        }
                        reminder1Date={programSetup?.sessionProgramData?.reminder1 ?? null}
                        reminder2Date={programSetup?.sessionProgramData?.reminder2 ?? null}
                        reminder3Date={programSetup?.sessionProgramData?.reminder3 ?? null}
                        showSnackbar={ShowSnackbarElement}
                    />
                ) : null}
            </Box>
        );
    }

    if (isError || paxIsError) {
        return (
            <Box>
                <Alert severity="error">
                    There was an error retrieving the assessment scoring status for this session.
                </Alert>
            </Box>
        );
    }

    // safety, should never get here
    return (
        <Stack width={1} spacing={2}>
            <Alert severity="error">
                Something went wrong retrieving assessment status information for this session.
            </Alert>
        </Stack>
    );
};

export default ScoringStatusSubPanel;
